import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Bell, CheckCheck, Megaphone, BookOpen, Shield, Clock,
  Loader2, Filter, LifeBuoy, Zap
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AdminLayout } from "../../components/layout/AdminLayout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const ICON_MAP = {
  announcement: { icon: Megaphone, color: "text-blue-600", bg: "bg-blue-50" },
  enrollment: { icon: BookOpen, color: "text-violet-600", bg: "bg-violet-50" },
  compliance: { icon: Shield, color: "text-emerald-600", bg: "bg-emerald-50" },
  support: { icon: LifeBuoy, color: "text-orange-600", bg: "bg-orange-50" },
  default: { icon: Bell, color: "text-slate-600", bg: "bg-slate-50" },
};

const LearnerNotificationsPage = () => {
  const { user } = useAuth();
  const variant = user?.role === "manager" ? "manager" : "learner";
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");

  const fetch = useCallback(async () => {
    try {
      const res = await api.get("/notifications?limit=100");
      setNotifications(res.data.notifications || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success("All marked as read");
    } catch { /* silent */ }
  };

  const filtered = notifications.filter(n => typeFilter === "all" || n.type === typeFilter);
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <AdminLayout title="Notifications" variant={variant}>
      <div className="space-y-6 max-w-3xl" data-testid="notifications-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Notifications</h1>
            <p className="text-muted-foreground text-sm">{unreadCount > 0 ? `${unreadCount} unread` : "All caught up!"}</p>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllRead} data-testid="mark-all-read-btn">
              <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px] h-9" data-testid="notif-type-filter">
              <Filter className="w-3 h-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="enrollment">Enrollment</SelectItem>
              <SelectItem value="compliance">Compliance</SelectItem>
              <SelectItem value="announcement">Announcements</SelectItem>
              <SelectItem value="support">Support</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-16 text-center"><Bell className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No notifications</p></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((n, i) => {
              const cfg = ICON_MAP[n.type] || ICON_MAP.default;
              const IconComp = cfg.icon;
              return (
                <motion.div key={n.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                  <Card
                    className={`cursor-pointer transition-colors ${!n.is_read ? "border-primary/20 bg-primary/[0.02]" : "hover:bg-muted/30"}`}
                    onClick={() => !n.is_read && markRead(n.id)}
                    data-testid={`notification-${i}`}
                  >
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg}`}>
                        <IconComp className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className={`text-sm ${!n.is_read ? "font-semibold" : ""}`}>{n.title}</h4>
                          {!n.is_read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                        </div>
                        {n.message && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>}
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {new Date(n.created_at).toLocaleString()}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default LearnerNotificationsPage;
